import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { FormContainer } from '@eika-kortbetjening/form-container';
import { EMPTY_PIN_ENTRY_FIELD, NewPinEntry, PinEntryFields } from '@eika-kortbetjening/pin-code';
import { Loader } from '@eika/loader';

import piwik from '@/piwik/piwik';
import { useSetNewPinFromAccountId } from '@/react-query/hooks/mutation/use-set-new-pin';
import { useAccountFromQueryParam } from '@/react-query/hooks/query/use-account';

import { useSession } from '@components/app/Session';
import Footer from '@components/footer/Footer';
import { NewPinEntryStatusMessage } from '@components/shared/NewPinEntryAttentionMessage';
import useText from '@hooks/use-text';
import { isValidPinLength, isValidPinPattern } from '@utils/pin-utils';

import './OrderCardFormNewPinEntryContainer.scss';

type PinEntryProps = {
    toNextStep(): void;
};

export const OrderCardFormNewPinEntryContainer = ({ toNextStep }: PinEntryProps) => {
    const [, setSearchParams] = useSearchParams();

    const [newPin, setNewPin] = useState<PinEntryFields>([
        EMPTY_PIN_ENTRY_FIELD,
        EMPTY_PIN_ENTRY_FIELD,
        EMPTY_PIN_ENTRY_FIELD,
        EMPTY_PIN_ENTRY_FIELD,
    ]);
    const [newPinInvalidError, setNewPinInvalidError] = useState<string | undefined>(undefined);

    const { pinNeedsFourCharsError, pinInvalidError, choosePinTitle, orderCardTitle, orderCardUserIsNotOwnerTitle } =
        useText();
    const { user } = useSession();
    const accountQuery = useAccountFromQueryParam();
    const setPinMutation = useSetNewPinFromAccountId(
        { accountId: accountQuery.data?.id ?? '' },
        {
            onSuccess: () => {
                setSearchParams(
                    (prev) => {
                        prev.set('nyPin', 'true');
                        return prev;
                    },
                    {
                        replace: true,
                    },
                );
            },
        },
    );
    const userIsOwnerOfTheAccount = user?.userId === accountQuery.data?.owner;

    useEffect(() => {
        setNewPinInvalidError(undefined);
    }, [newPin]);

    if (accountQuery.isPending) {
        return <Loader />;
    }
    if (accountQuery.isError) {
        return null;
    }

    const submitNewPinEntry = () => {
        if (!isValidPinLength(newPin)) {
            setNewPinInvalidError(pinNeedsFourCharsError);
            return;
        }

        if (!isValidPinPattern(newPin)) {
            setNewPinInvalidError(pinInvalidError);
            return;
        }

        setPinMutation.mutate({ newPin });
    };

    return (
        <FormContainer
            className="order-card-new-pin-entry-form"
            data-track-id={piwik.replaceCard.newPinForm.dataTrackId}
            data-track-linkname={piwik.replaceCard.newPinForm.dataTrackLinkname}
            id={piwik.replaceCard.newPinForm.dataTrackId}
        >
            <FormContainer.Header title={userIsOwnerOfTheAccount ? orderCardTitle : orderCardUserIsNotOwnerTitle} />
            {choosePinTitle}
            <FormContainer.Content>
                <div className="order-card-new-pin-entry-form__form">
                    <div className="order-card-new-pin-entry-form__form__new-pin-entry">
                        <NewPinEntry
                            newPin={newPin}
                            setNewPin={setNewPin}
                            hasError={typeof newPinInvalidError !== 'undefined'}
                            piwik={piwik}
                        />
                    </div>
                    <NewPinEntryStatusMessage newPinInvalidError={newPinInvalidError} setPinQuery={setPinMutation} />
                </div>
            </FormContainer.Content>
            <FormContainer.Footer>
                <Footer
                    next={setPinMutation.isSuccess ? toNextStep : submitNewPinEntry}
                    disabled={setPinMutation.isPending}
                    canGoBack
                />
            </FormContainer.Footer>
        </FormContainer>
    );
};
